/**
 * Sign in — both web portals (design approved 26 Sep 2026).
 *
 * One question at a time (`useEmailFirstSignIn`, @tonnie/sdk):
 *
 *   email (or LinkedIn / Google / Microsoft / Apple)
 *     → password — "email me a code instead" when the account can
 *     → a code  — straight away for an account with no password
 *     → a code to prove a new device (Clerk's Client Trust), or the
 *       authenticator (the portal's `allowTotp`)
 *     → the portal's `afterSignIn`
 *
 * An invitation (`?invite=1&email=`) names its masked address in a callout; a
 * plain `?email=` fills the first step. A browser already signed in
 * (another portal on the same Clerk instance) gets the AlreadySignedInCard
 * instead of a form Clerk would refuse.
 *
 * Words are ours, never Clerk's (`useAuthErrorMessage`); only an unknown
 * failure is reported.
 *
 * Observability (Part 22): PAGE_VIEWED; AUTH_LOGIN_SUBMITTED on the email
 * step; AUTH_LOGIN_SUCCEEDED + AUTH_SIGNED_IN on success; AUTH_LOGIN_FAILED
 * with Clerk's code on every failure.
 */

import { useAuth } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, Mail } from '@tonnie/assets';
import { classifyClerkError } from '@tonnie/foundation';
import { useAuthErrorMessage, usePasswordRevealLabels, useTranslation } from '@tonnie/locale';
import { useAnalytics, useErrorCapture, usePageView } from '@tonnie/observability';
import {
  emailStepSchema,
  signInPasswordSchema,
  type EmailStepInput,
  type SignInPasswordInput,
} from '@tonnie/schemas';
import { useEmailFirstSignIn, type SignInOutcome } from '@tonnie/sdk';
import {
  Alert,
  AlertDescription,
  AuthDivider,
  AuthEmailChip,
  Button,
  Input,
  Label,
  PasswordInput,
} from '@tonnie/ui';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'wouter';

import { goToDestination, useAccountPortal } from '../portal';
import { AccountAuthLayout } from './AccountAuthLayout';
import { AlreadySignedInCard } from './AlreadySignedInCard';
import { CodeStep } from './CodeStep';
import { SocialSignInSection } from './SocialSignInSection';

export function SignInView() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { portal, allowTotp, afterSignIn } = useAccountPortal();
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const flow = useEmailFirstSignIn({ allowTotp });
  const describeAuthError = useAuthErrorMessage();
  const { track } = useAnalytics();
  const { captureException } = useErrorCapture();
  usePageView('login');

  const [error, setError] = useState<ReactNode>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  // An invitation's "Sign in to accept" forwards its MASKED address with
  // ?invite=1 — named in a callout, never pre-filled (a masked address is not
  // an address). A plain ?email= (from sign-up's "sign in instead") fills it.
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const invitationEmail = params.get('invite') === '1' ? params.get('email') : null;
  const prefill = invitationEmail ? '' : (params.get('email') ?? '');

  /** One place turns an outcome into what happens next. */
  const settle = useCallback(
    (result: SignInOutcome, fallback: string, show: (message: string) => void): void => {
      switch (result.outcome) {
        case 'next':
          setError(null);
          setCodeError(null);
          return;
        case 'complete':
          track('AUTH_LOGIN_SUCCEEDED', { method: 'email' });
          track('AUTH_SIGNED_IN', { method: 'clerk' });
          goToDestination(afterSignIn(), setLocation);
          return;
        case 'failed': {
          const failure = describeAuthError(result.error, fallback);
          track('AUTH_LOGIN_FAILED', { method: 'email', error_code: failure.code });
          if (!failure.expected) {
            captureException(
              result.error instanceof Error ? result.error : new Error(String(result.error)),
              { page: 'login', step: flow.state.step }
            );
          }
          show(failure.message);
          return;
        }
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled sign-in outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [describeAuthError, track, captureException, setLocation, afterSignIn, flow.state.step]
  );

  if (clerkLoaded && isSignedIn) {
    return <AlreadySignedInCard brand="sign-in" />;
  }

  const { state } = flow;

  switch (state.step) {
    case 'email':
      return (
        <AccountAuthLayout
          brand="sign-in"
          title={t('auth.flow.signIn.title')}
          description={
            portal === 'business'
              ? t('auth.flow.signIn.descriptionBusiness')
              : t('auth.flow.signIn.descriptionCitizen')
          }
        >
          {invitationEmail && (
            <Alert data-testid="auth-login-invitation-callout">
              <Mail className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('auth.flow.invitation.signIn', { email: invitationEmail })}
              </AlertDescription>
            </Alert>
          )}
          <SocialSignInSection mode="sign-in" />
          <AuthDivider label={t('auth.flow.orWithEmail')} />
          <EmailStep
            defaultEmail={prefill}
            error={error}
            ready={flow.ready}
            onSubmit={async (email) => {
              setError(null);
              track('AUTH_LOGIN_SUBMITTED', { method: 'email' });
              const result = await flow.identify(email);
              if (
                result.outcome === 'failed' &&
                classifyClerkError(result.error).kind === 'account_not_found'
              ) {
                track('AUTH_LOGIN_FAILED', { method: 'email', error_code: 'account_not_found' });
                setError(
                  <>
                    {t('auth.flow.signIn.noAccount')}{' '}
                    <Link
                      href={`/signup?email=${encodeURIComponent(email)}`}
                      className="font-semibold underline"
                    >
                      {t('auth.flow.signIn.createAccount')}
                    </Link>
                  </>
                );
                return;
              }
              settle(result, t('auth.errors.unableToProcess'), setError);
            }}
          />
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.flow.signIn.newHere')}{' '}
            <Link
              href="/signup"
              className="font-semibold text-primary-accessible hover:underline"
              data-testid="auth-login-signup-link"
            >
              {t('auth.flow.signIn.createAccount')}
            </Link>
          </p>
        </AccountAuthLayout>
      );

    case 'password':
      return (
        <AccountAuthLayout
          brand="sign-in"
          title={t('auth.flow.password.title')}
          description={
            <AuthEmailChip
              email={state.email}
              changeLabel={t('auth.flow.change')}
              onChange={flow.restart}
            />
          }
          onBack={flow.restart}
        >
          <PasswordStep
            error={error}
            onSubmit={async (password) => {
              setError(null);
              settle(
                await flow.submitPassword(password),
                t('auth.errors.invalidCredentials'),
                setError
              );
            }}
          />
          {state.canUseCode && (
            <>
              <AuthDivider label={t('auth.flow.or')} />
              <Button
                type="button"
                variant="outline"
                className="h-11 gap-2 text-base font-medium"
                onClick={async () => {
                  setError(null);
                  settle(await flow.sendCodeInstead(), t('auth.errors.unableToProcess'), setError);
                }}
                data-testid="auth-login-use-code"
              >
                <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                {t('auth.flow.password.codeInstead')}
              </Button>
            </>
          )}
        </AccountAuthLayout>
      );

    case 'code':
      return (
        <AccountAuthLayout
          brand="sign-in"
          title={t('auth.flow.code.title')}
          description={
            state.factor === 'second'
              ? t('auth.flow.code.newDevice', { email: state.email })
              : t('auth.flow.code.description', { email: state.email })
          }
          onBack={flow.restart}
        >
          <CodeStep
            label={t('auth.flow.code.label')}
            submitLabel={t('auth.flow.code.submit')}
            error={codeError}
            testIdPrefix="auth-login"
            onSubmit={async (code) =>
              settle(
                await flow.submitCode(code),
                t('auth.errors.invalidVerificationCode'),
                setCodeError
              )
            }
            onResend={async () => {
              const result = await flow.resendCode();
              settle(result, t('auth.errors.unableToResend'), setCodeError);
              return result.outcome === 'next';
            }}
          />
        </AccountAuthLayout>
      );

    case 'totp':
      return (
        <AccountAuthLayout
          brand="sign-in"
          title={t('auth.flow.totp.title')}
          description={t('auth.flow.totp.description')}
          onBack={flow.restart}
        >
          <CodeStep
            label={t('auth.flow.totp.label')}
            submitLabel={t('auth.flow.code.submit')}
            error={codeError}
            testIdPrefix="auth-login-totp"
            onSubmit={async (code) =>
              settle(
                await flow.submitCode(code),
                t('auth.errors.invalidVerificationCode'),
                setCodeError
              )
            }
          />
        </AccountAuthLayout>
      );

    default: {
      const exhaustive: never = state;
      throw new Error(`Unhandled sign-in step: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function FormError({ message, testId }: { message: ReactNode; testId: string }) {
  if (!message) return null;
  return (
    <Alert variant="destructive" data-testid={testId}>
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function EmailStep({
  defaultEmail,
  error,
  ready,
  onSubmit,
}: {
  defaultEmail: string;
  error: ReactNode;
  ready: boolean;
  onSubmit: (email: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailStepInput>({
    resolver: zodResolver(emailStepSchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <form
      onSubmit={handleSubmit(({ email }) => onSubmit(email))}
      noValidate
      className="flex flex-col gap-4"
      data-testid="auth-login-form"
    >
      <FormError message={error} testId="auth-login-error" />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="login-email" className="text-sm font-medium">
          {t('auth.flow.emailLabel')}
        </Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          placeholder={t('auth.flow.emailPlaceholder')}
          className="h-11 text-base"
          aria-invalid={!!errors.email}
          data-testid="auth-login-email"
          {...register('email')}
        />
        {errors.email?.message && (
          <p className="text-sm text-destructive-accessible" data-testid="auth-login-email-error">
            {t(errors.email.message)}
          </p>
        )}
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting || !ready}
        data-testid="auth-login-continue"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.flow.continue')}
      </Button>
    </form>
  );
}

function PasswordStep({
  error,
  onSubmit,
}: {
  error: ReactNode;
  onSubmit: (password: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const reveal = usePasswordRevealLabels();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInPasswordInput>({ resolver: zodResolver(signInPasswordSchema) });

  return (
    <form
      onSubmit={handleSubmit(({ password }) => onSubmit(password))}
      noValidate
      className="flex flex-col gap-4"
    >
      <FormError message={error} testId="auth-login-error" />
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="login-password" className="text-sm font-medium">
            {t('auth.flow.password.label')}
          </Label>
          <Link
            href="/forgot-password"
            className="text-sm font-semibold text-primary-accessible hover:underline"
            data-testid="auth-login-forgot-link"
          >
            {t('auth.flow.password.forgot')}
          </Link>
        </div>
        <PasswordInput
          id="login-password"
          revealLabels={reveal}
          autoComplete="current-password"
          autoFocus
          className="h-11 text-base"
          aria-invalid={!!errors.password}
          data-testid="auth-login-password"
          {...register('password')}
        />
        {errors.password?.message && (
          <p
            className="text-sm text-destructive-accessible"
            data-testid="auth-login-password-error"
          >
            {t(errors.password.message)}
          </p>
        )}
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting}
        data-testid="auth-login-submit"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.flow.password.submit')}
      </Button>
    </form>
  );
}
